import { VideoData } from '@lumen5/luminary';

import { VoiceoverRegistry } from '../registries/VoiceoverRegistry';
import type { ToolDefinition, ToolExecutionContext } from '../types';

import { AddAIVoiceoverSceneGroupTool } from './AddAIVoiceoverSceneGroupTool';
import { AddTalkingHeadSceneGroupTool } from './AddTalkingHeadSceneGroupTool';
import { ConversationTool } from './ConversationTool';
import { computeSceneGroupShortIds, resolveInsertSlotAfter } from './scene-group-addressing';
import { SearchUploadedVoiceoversTool } from './SearchUploadedVoiceoversTool';

import type { ImmutableAudioAsset } from 'js/src/data/media';
import { isImmutableAudioAsset } from 'js/src/data/media/media-types';
import { getUploadedAssetByUuid } from 'js/src/data/uploads/uploads-selectors';
import type { RootState } from 'js/src/libs/RootState';
import {
    createNewVoicedrivenSceneGroupOperation,
    deleteSceneGroupOperation,
    setAndApplyVoiceoverAssetForSceneGroupOperation,
} from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

interface AddRecordedVoiceoverSceneGroupArgs {
    after_scene_group_id?: string;
    audio_uuid: string;
    language_code?: string;
}

export class AddRecordedVoiceoverSceneGroupTool extends ConversationTool<AddRecordedVoiceoverSceneGroupArgs> {
    public static readonly toolName = 'add_recorded_voiceover_scene_group';

    public override getToolDefinition(): ToolDefinition {
        return {
            type: 'function',
            function: {
                name: AddRecordedVoiceoverSceneGroupTool.toolName,
                description: `Add a new voiceover scene group driven by an uploaded voiceover AUDIO file (e.g. a human recording). The audio becomes the narration track and its speech is transcribed into a synced transcript. The audio_uuid may come from the "### AUDIO: ... | UUID: ... ###" metadata of a file attached to the chat, or from a result returned by ${SearchUploadedVoiceoversTool.toolName}. To generate AI voiceover from text instead, use ${AddAIVoiceoverSceneGroupTool.toolName}.`,
                parameters: {
                    type: 'object',
                    properties: {
                        after_scene_group_id: {
                            type: 'string',
                            description:
                                'The stable scene_group_id of the scene group the new voiceover scene group should be inserted immediately after. Omit to insert it at the very start of the video.',
                        },
                        audio_uuid: {
                            type: 'string',
                            description: `The UUID of the uploaded voiceover audio file. Either the UUID from the "### AUDIO: ... | UUID: ... ###" metadata of an attached file, or the uuid of a result returned by ${SearchUploadedVoiceoversTool.toolName}.`,
                        },
                        language_code: {
                            type: 'string',
                            description:
                                'Optional language code for caption generation (e.g., "en", "es"). Auto-detected if omitted.',
                        },
                    },
                    required: ['audio_uuid'],
                },
            },
        };
    }

    public override getToolDisplayName(): string {
        return 'Adding voiceover scene group';
    }

    public get statusIcon(): string {
        return 'mic';
    }

    public async execute(args: AddRecordedVoiceoverSceneGroupArgs, context: ToolExecutionContext) {
        const { after_scene_group_id, audio_uuid, language_code } = args;
        const { parentActionGroup } = context;

        // Resolve the insert slot up front (fail-closed on an unknown anchor id).
        const slot = resolveInsertSlotAfter(this.luminary, after_scene_group_id);

        let voiceoverAsset: ImmutableAudioAsset;
        const uploadedAsset = getUploadedAssetByUuid(this._getState(), audio_uuid);
        if (uploadedAsset) {
            // Attached to this chat — unchanged behavior.
            if (!isImmutableAudioAsset(uploadedAsset)) {
                return `Error: The uploaded file ${audio_uuid} is not an audio file. For an uploaded talking-head video, use ${AddTalkingHeadSceneGroupTool.toolName} instead.`;
            }
            voiceoverAsset = uploadedAsset;
        } else {
            // Not a this-session attachment — try to resolve a previously-uploaded
            // voiceover surfaced by search_uploaded_voiceovers.
            const voiceoverRegistry = VoiceoverRegistry.getInstance();
            const registryId = await voiceoverRegistry.addItemByUuid(audio_uuid);
            const resolvedAsset = registryId
                ? voiceoverRegistry.getAudioAssetData(registryId)
                : null;
            if (!resolvedAsset) {
                return `Error: Could not find an uploaded audio file with UUID ${audio_uuid}. Make sure the audio file was attached to the chat and finished uploading, or was returned by ${SearchUploadedVoiceoversTool.toolName}.`;
            }
            voiceoverAsset = resolvedAsset;
        }
        if (!voiceoverAsset.get('has_voiceover')) {
            return `Error: The uploaded audio does not appear to contain spoken voiceover. Please upload an audio file with clear speech.`;
        }

        try {
            const {
                createNewVoicedrivenSceneGroup,
                setAndApplyVoiceoverAssetForSceneGroup,
                deleteSceneGroup,
            } = makeOperationsCallable(
                {
                    createNewVoicedrivenSceneGroupOperation,
                    setAndApplyVoiceoverAssetForSceneGroupOperation,
                    deleteSceneGroupOperation,
                },
                this.luminary,
                this.dispatch,
                this.getState as () => RootState,
            );

            const { sceneGroupId } = createNewVoicedrivenSceneGroup(
                VideoData.CATEGORY_VOICEOVER,
                slot,
                { deleteOtherScenes: false, setPlaceholderAsset: false, hasAvatar: false },
                parentActionGroup,
                false,
            );

            // Applies the uploaded audio as the voiceover. The transcript is omitted so
            // the operation fetches (or generates) it from the audio asset itself.
            // recommendOverlays is false — the agent adds overlays explicitly via
            // add_voiceover_scene. This operation checkpoints the video itself.
            const hasAssetBeenApplied = await setAndApplyVoiceoverAssetForSceneGroup({
                sceneGroupId,
                audioAssetData: voiceoverAsset,
                avatarAssetData: null,
                captionsLanguage: language_code ?? null,
                forceCaptionGeneration: false,
                selectedBlueprintId: null,
                generateRecommendedVideoResponse: null,
                sceneRecommendations: null,
                recommendOverlays: false,
                isGlobalVideo: true,
                systemTriggered: false,
                parentActionGroup,
            });

            if (!hasAssetBeenApplied) {
                deleteSceneGroup(sceneGroupId, false, parentActionGroup);
                return `Error: the voiceover audio could not be applied to the new scene group.`;
            }

            this.startBaseSceneBuilding(sceneGroupId);

            const durationSeconds = voiceoverAsset.get('duration');
            const durationInfo =
                typeof durationSeconds === 'number'
                    ? ` Voiceover duration: ${durationSeconds.toFixed(1)}s.`
                    : '';

            const shortId =
                computeSceneGroupShortIds(this.luminary.sceneGroups.map((group) => group.id)).get(
                    sceneGroupId,
                ) ?? sceneGroupId;
            const placement =
                after_scene_group_id && after_scene_group_id.trim() !== ''
                    ? `right after scene group ${after_scene_group_id}`
                    : 'at the start of the video';
            return `Success: Added new voiceover scene group ${shortId} ${placement} from uploaded audio.${durationInfo}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error adding voiceover scene group: ${errorMessage}`;
        }
    }
}
